/**
 * Security utilities for the Memory Hub.
 * Follows OWASP and WASG 3 guidelines for input sanitization and secure data handling.
 */

/**
 * Sanitizes a string to prevent XSS (Cross-Site Scripting).
 * Replaces critical characters with their HTML entity equivalents.
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    "/": '&#x2F;',
  };
  const reg = /[&<>"'/]/ig;
  return input.replace(reg, (match) => map[match]);
};

/**
 * Decodes HTML entities back to their original characters.
 * Use this when displaying sanitized content in standard React text nodes.
 */
export const decodeEntities = (input: string): string => {
  if (!input) return '';
  const map: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#x27;': "'",
    '&#x2F;': '/',
  };
  return input.replace(/&amp;|&lt;|&gt;|&quot;|&#x27;|&#x2F;/g, (match) => map[match]);
};

/**
 * Validates if a string contains potential injection patterns.
 * Useful for pre-check before saving sensitive context.
 */
export const hasInjectionRisk = (input: string): boolean => {
  const injectionPatterns = [
    /<script\b[^>]*>([\s\S]*?)<\/script>/gim,
    /javascript:/gim,
    /onerror/gim,
    /onload/gim,
    /onclick/gim,
    /eval\(/gim,
  ];
  return injectionPatterns.some((pattern) => pattern.test(input));
};

/**
 * Securely hashes a string (simulated for front-end only environments).
 * In a production app, sensitive hashing should happen server-side.
 */
export const hashData = async (data: string): Promise<string> => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Guideline Check for WASG 3 Compliance.
 * This can be used to drive a security dashboard.
 */
export const WASG_CHECKLIST = [
  { id: 'SC-1', category: 'Secure Communication', description: 'Ensure all data transit uses TLS 1.2+.', status: 'Compliant' },
  { id: 'IV-1', category: 'Input Validation', description: 'All user/agent inputs must be sanitized.', status: 'Active' },
  { id: 'SM-1', category: 'Session Management', description: 'Secure session tokens with HttpOnly and Semicolon.', status: 'N/A (Frontend)' },
  { id: 'DS-1', category: 'Data Security', description: 'Sensitive local storage data must be encrypted.', status: 'In Progress' },
  { id: 'EH-1', category: 'Error Handling', description: 'Do not expose stack traces or system info.', status: 'Compliant' },
];
